import transactionsServices from '@/api/transactionsServices';
import { RootState } from '@/app/store';
import { IbTransaction } from '@/interfaces/transaction';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { convertToUtc } from '@/utils/dateHelpers';

interface FilterParams {
  page: number | undefined;
  size: number | undefined;
  accountId: string;
  status: string;
  startDate: string;
  endDate: string;
}

interface MetaData {
  totalPages: number;
  totalElements: number;
}

interface Validator {
  userId: string;
  username: string;
  firstName: string;
  lastName: string;
  approvedAt: string;
}

interface PendingTransactionsState {
  currFilters: string;
  pendingTransactions: { [key: string]: IbTransaction[] };
  metaData: { [key: string]: MetaData };
  loading: boolean;
  detailLoading: boolean;
  params: FilterParams;
  validatorsByTransId: { [key: string]: Validator[] };
  ibTransaction: IbTransaction | null;
  selectedPicture: { [key: string]: string };
  pictureLoading: boolean;
}

const initialState: PendingTransactionsState = {
  currFilters: '',
  pendingTransactions: {},
  metaData: {},
  loading: false,
  detailLoading: false,
  validatorsByTransId: {},
  ibTransaction: {} as IbTransaction,
  selectedPicture: {},
  pictureLoading: false,
  params: {
    page: undefined,
    size: undefined,
    accountId: '',
    status: '',
    startDate: '',
    endDate: '',
  },
};

export const fetchPendingTransactions = createAsyncThunk(
  'pendingTransactions/get',
  async ({ size, page, accountId, status, startDate, endDate }: FilterParams) => {
    const normalizeStartDate = convertToUtc(startDate);
    const normalizeEndDate = convertToUtc(endDate);
    const params = {
      size,
      page: page! - 1,
      accountId,
      ...(status && { status }),
      startDate: normalizeStartDate,
      endDate: normalizeEndDate,
    };

    const result = await transactionsServices.loadPendingTransactions(params);
    return result;
  }
);

export const fetchTransactionDetails = createAsyncThunk('pendingTransactions/fetch', async (transactionId: string) => {
  const result = await transactionsServices.loadDetails(transactionId);
  return result;
});

export const fetchPendingTransImage = createAsyncThunk(
  'pendingTransactions/fetchImage',
  async ({ id, fileName, contentDisposition = 'inline' }: { id: string; fileName: string; contentDisposition?: string }, thunkAPI) => {
    try {
      const result = await transactionsServices.fetchPendingTransComplianceDoc(id, { fileName, contentDisposition });
      return result;
    } catch (error: any) {
      let responseObj = await error;
      const errorMessage = responseObj.message;
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const pendingTransactionsSlice = createSlice({
  name: 'pendingTransactions',
  initialState,
  reducers: {
    refresh: () => initialState,
    setSelectedIBTrans: (state, action: PayloadAction<IbTransaction | null>) => {
      state.ibTransaction = action.payload;
    },
    setDetailLoading: (state, action: PayloadAction<boolean>) => {
      state.detailLoading = action.payload;
    },
    handleParamsChange: (state, action: PayloadAction<FilterParams>) => {
      const stringifyFilters = JSON.stringify(action.payload);
      state.currFilters = stringifyFilters;
      state.params.page = action.payload.page;
      state.params.size = action.payload.size;
      state.params.accountId = action.payload.accountId;
      state.params.status = action.payload.status;
      state.params.startDate = action.payload.startDate;
      state.params.endDate = action.payload.endDate;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchPendingTransactions.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchPendingTransactions.fulfilled, (state, action) => {
      const { payload } = action;
      state.pendingTransactions[state.currFilters] = payload.data;
      state.metaData[state.currFilters] = { totalPages: payload.totalPages, totalElements: payload.totalElements };
      state.loading = false;
    });
    builder.addCase(fetchPendingTransactions.rejected, state => {
      state.loading = false;
    });
    builder.addCase(fetchTransactionDetails.pending, state => {
      state.detailLoading = true;
    });
    builder.addCase(fetchTransactionDetails.fulfilled, (state, action) => {
      const transId = state.ibTransaction?.id;
      if (transId) {
        state.validatorsByTransId[transId] = action.payload.validators;
        if (action.payload.complianceDocuments && state.ibTransaction) {
          state.ibTransaction.complianceDocuments = action.payload.complianceDocuments;
        }
      }
      state.detailLoading = false;
    });
    builder.addCase(fetchTransactionDetails.rejected, state => {
      state.detailLoading = false;
    });
    builder.addCase(fetchPendingTransImage.pending, state => {
      state.pictureLoading = true;
    });
    builder.addCase(fetchPendingTransImage.fulfilled, (state, action) => {
      const {
        payload,
        meta: {
          arg: { id, fileName },
        },
      } = action;
      state.selectedPicture[`${id}-${fileName}`] = payload;
      state.pictureLoading = false;
    });
    builder.addCase(fetchPendingTransImage.rejected, state => {
      state.pictureLoading = false;
    });
  },
});

export const { handleParamsChange, refresh, setSelectedIBTrans } = pendingTransactionsSlice.actions;

export const selectPendingTransactions = (state: RootState) => state.pendingTransactions;

export default pendingTransactionsSlice.reducer;
